import React, { forwardRef, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import moment from "moment";
import "../../pages/quotation/Forecast.scss";
import "react-datepicker/dist/react-datepicker.css";
import ErrorMessage from "../common/ErrorMessage";
import SaveSuccessModel from "../common/SaveSuccessModel";
import { AsyncCreateEntry } from "../../providers/dataProvider";
import { CurrencyConvertion, FormatValueDecimal, calculateNetPrice } from "../../utils/Validate";

const ExampleCustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    value={value}
    placeholder={placeholder}
    className="example-custom-input form-control"
    onClick={onClick}
    ref={ref}
    readOnly
  />
));

const BulkForecastModal = ({
  show,
  onHide,
  items = [],
  quoteId,
  currencyDetail,
  conversionRate,
  existingForecasts = [],
  onSaved,
}) => {
  const [forecastDate, setForecastDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dateError, setDateError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [errorShow, setErrorShow] = useState({
    show: false,
    errorMessage: "",
  });

  useEffect(() => {
    if (!show) {
      setForecastDate(null);
      setDateError("");
      setSaving(false);
    }
  }, [show]);

  const selectedItems = useMemo(
    () => (items || []).filter(Boolean),
    [items]
  );

  const selectedProductIds = useMemo(
    () => new Set(selectedItems.map((item) => String(item?.id))),
    [selectedItems]
  );

  const quotedAmount = useMemo(
    () =>
      selectedItems.reduce(
        (sum, item) => sum + Number(calculateNetPrice(item) || 0),
        0
      ),
    [selectedItems]
  );

  const forecastedAmount = useMemo(
    () =>
      (existingForecasts || []).reduce((sum, forecast) => {
        if (!selectedProductIds.has(String(forecast?.quoteProductId))) {
          return sum;
        }

        return sum + Number(forecast?.forecastedAmount || 0);
      }, 0),
    [existingForecasts, selectedProductIds]
  );

  const balanceAmount = quotedAmount - forecastedAmount;
  const totalQuantity = useMemo(
    () =>
      selectedItems.reduce(
        (sum, item) => sum + Number(item?.quantity || 0),
        0
      ),
    [selectedItems]
  );

  const handleClose = () => {
    if (saving) return;
    onHide?.();
  };

  const handleSave = async () => {
    if (!forecastDate) {
      setDateError("Forecast date is required");
      return;
    }

    setDateError("");
    setSaving(true);

    try {
      const forecastedDate = moment(forecastDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
      const createdBy = localStorage.getItem("authidentity");

      for (const item of selectedItems) {
        const existingForecast = (existingForecasts || []).find(
          (forecast) => String(forecast?.quoteProductId) === String(item?.id)
        );

        const payload = {
          id: existingForecast?.id || 0,
          quotationId: quoteId,
          quoteProductId: item?.id,
          createdAt: new Date(),
          createdBy,
          productForecasts: [
            {
              id: existingForecast?.productForecasts?.[0]?.id || 0,
              forecastId: existingForecast?.id || 0,
              quoteProductId: item?.id,
              expectedAmount: Number(calculateNetPrice(item) || 0),
              expectedQuantity: Number(item?.quoteQuantity ?? item?.quantity ?? 0),
              expectedDate: forecastedDate,
              revisionNo: (existingForecast?.productForecasts?.[0]?.revisionNo || 0) + 1,
              histories: [
                {
                  id: 0,
                  forecastedDate,
                },
              ],
            },
          ],
        };

        const result = await AsyncCreateEntry(
          "forecast",
          payload,
          "Forecast saved successfully",
          "Failed to save forecast"
        );

        if (result?.status && result.status >= 400) {
          throw new Error(result?.message || "Failed to save forecast");
        }
      }

      setCreateMsg({
        message: "Forecast saved successfully",
      });
      setShowSuccess(true);
    } catch (error) {
      setErrorShow({
        show: true,
        errorMessage:
          error?.message ||
          "Failed to save forecast",
      });
    } finally {
      setSaving(false);
    }
  };

  const SuccessDoneClick = () => {
    setShowSuccess(false);
    setCreateMsg(null);
    onHide?.();
    onSaved?.(selectedItems);
  };

  const ErrorDoneClick = () => {
    setErrorShow({
      show: false,
      errorMessage: "",
    });
  };

  return (
    <>
      <Modal
        size="sm"
        show={show}
        onHide={handleClose}
        className="forecastRightSideModalPopup drawer right-align"
      >
        <Modal.Header>
          <h5 className="modal-title">
            Selected{" "}
            {selectedItems?.length > 0
              ? `(${selectedItems.length} mark${selectedItems.length === 1 ? "" : "s"})`
              : ""}
          </h5>
          <i className="fas fa-times" onClick={handleClose}></i>
        </Modal.Header>
        <Modal.Body>
          <div className="row modal-row mt-2">
            <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-4 col-xs-4">
              <div className="quoted-col">
                <p>Quoted ({currencyDetail?.shortCode})</p>
                <h4>
                  {FormatValueDecimal(
                    CurrencyConvertion(quotedAmount, conversionRate, currencyDetail)
                  )}
                </h4>
              </div>
            </div>
            <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-4 col-xs-4">
            <div className="forecasted-col">
                <p>Forecasted ({currencyDetail?.shortCode})</p>
                <h4>
                  {FormatValueDecimal(
                    CurrencyConvertion(forecastedAmount, conversionRate, currencyDetail)
                  )}
                </h4>
            </div>
            </div>
            <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-4 col-xs-4">
              <div className="balance-col">
                <p>Balance ({currencyDetail?.shortCode})</p>
                <h4>
                  {FormatValueDecimal(
                    CurrencyConvertion(balanceAmount, conversionRate, currencyDetail)
                  )}
                </h4>
              </div>
            </div>
          </div>

          {/* <div className="header mt-3">
            <h6>Selected Products</h6>
          </div> */}

          {/* <div className="table-responsive mt-2">
            <table className="table table-bordered m-0 text-center">
              <thead>
                <tr>
                  <th>Mark</th>
                  <th>Qty</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems?.map((item) => (
                  <tr key={item?.id}>
                    <td>{item?.markName}</td>
                    <td>{item?.quantity}</td>
                    <td>
                      {FormatValueDecimal(
                        CurrencyConvertion(
                          calculateNetPrice(item),
                          conversionRate,
                          currencyDetail
                        )
                      )}
                    </td>
                    <td>{forecastDate ? moment(forecastDate).format("DD-MMM-YYYY") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div> */}

          <div className="mt-3">
            <label>Date</label>
            <DatePicker
              selected={forecastDate}
              onChange={(date) => {
                setForecastDate(date);
                setDateError("");
              }}
              customInput={<ExampleCustomInput placeholder="dd/MM/yyyy" />}
              dayClassName={() => "example-datepicker-day-class"}
              popperClassName="example-datepicker-class"
              todayButton="TODAY"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              minDate={new Date()}
            />
            {dateError && <p className="form-error">{dateError}</p>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="forecast-modal-actions">
            <button type="button" className="btn cancel-btn" onClick={handleClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn save-btn"
              onClick={handleSave}
              disabled={!forecastDate || saving || selectedItems.length === 0}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      <SaveSuccessModel
        show={showSuccess}
        createMsg={createMsg}
        setShow={setShowSuccess}
        SuccessDoneClick={SuccessDoneClick}
      />
      <ErrorMessage
        Show={errorShow?.show}
        errorMessage={errorShow?.errorMessage}
        ErrorDoneClick={ErrorDoneClick}
      />
    </>
  );
};

export default BulkForecastModal;
